import React, { useCallback, useEffect, useState } from 'react';
import {
  Route,
  Navigate,
  Routes,
  Outlet,
  useParams,
  useLocation,
} from 'react-router-dom';
import { useLocalStorage, useMedia } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';

import {
  Modal,
  PageContainer,
  MainMenu,
  NotificationsListener,
  AppWidget,
  BillingAttentionBanner,
} from 'components';
import { AppShell } from 'components/BillingAttentionBanner/styled';
import ProfileRouter from './ProfileRouter';
import CommunityRouterWrapper from './CommunityRouterWrapper';
import CommunityLandingWrapper from './CommunityLandingWrapper';
import {
  getCommunitiesLoaded,
  getCommunitiesLoading,
} from 'store/selectors/communities';
import { getAuthUser } from 'store/selectors/auth';
import { doRequestCommunities } from 'store/actionCreators/communities';
import { breakpoints } from 'theme';
import { getChatNotification, getChatUser } from 'store/selectors/chat';
import { useToasts } from 'react-toast-notifications';
import { COMMUNITY_ROUTES, PROFILE_ROUTES, ROUTES } from 'configs';
import { Chat, ChatMessage } from 'store/reducers/chat';
import { IN_APP_NOTIFICATION_TYPE } from 'types';
import { doAddNotification } from 'store/actionCreators/profile';
import { commonGetStorageCommunity } from 'utils/commonStorage';
import { getInAppToastAppearance } from 'utils/notificationAppearance';
import {
  CancelSubscriptionPage,
  ChangePlanPage,
  EnterpriseCheckoutPage,
  SubscriptionInner,
} from 'pages';

/** Legacy `/profile/subscription/plans/:id` → canonical checkout URL. */
const SubscriptionPlansToChangePlanCheckoutRedirect = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const location = useLocation();
  return (
    <Navigate
      to={`/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}/${subscriptionId}${location.search}`}
      replace
      state={location.state}
    />
  );
};

/** Legacy `/profile/subscription/plans` → plan selection. */
const SubscriptionPlansListingRedirect = () => (
  <Navigate
    to={`/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`}
    replace
  />
);

const PageContainerWrapper = () => {
  const isWide = useMedia(breakpoints.md);
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const chatUser = useSelector(getChatUser);
  const { communityId } = useParams();
  const notificationData = useSelector(getChatNotification);
  const communitiesLoaded = useSelector(getCommunitiesLoaded);
  const communitiesLoading = useSelector(getCommunitiesLoading);
  const userProfile = useSelector(getAuthUser);
  const [appWidgetVisible, setAppWidgetVisible] = useState<boolean>(false);
  const [appWidgetStatus, setAppWidgetStatus] = useLocalStorage<boolean>(
    'appWidgetShown',
    false,
  );

  useEffect(() => {
    if (userProfile?.id && !communitiesLoaded && !communitiesLoading) {
      dispatch(doRequestCommunities());
    }
  }, [userProfile, communitiesLoaded, communitiesLoading, dispatch]);

  useEffect(() => {
    if (!isWide && !appWidgetStatus) {
      setAppWidgetStatus(true);
      setTimeout(() => setAppWidgetVisible(true), 2000);
    }
  }, [appWidgetStatus]);

  const sendNotificationSticky = useCallback((notification) => {
    addToast(notification, {
      appearance: getInAppToastAppearance(notification.notificationType),
      autoDismiss: false,
    });
  }, []);

  const createAndDispatchNotification = async (
    messages: ChatMessage,
    chatId: string,
    userData: Chat | null,
  ) => {
    try {
      if (!userData) return;
      const link = communityId
        ? `${ROUTES.COMMUNITY}/${communityId}/${COMMUNITY_ROUTES.CHAT}?talkId=${chatId}`
        : `/profile/${PROFILE_ROUTES.CHAT}?talkId=${chatId}`;

      const notification = {
        id: messages.senderId,
        userId: userProfile.userId,
        message: `💬  •  ${
          userData?.isGroupChat
            ? userData.chatName
            : `${userData.peerUsers.firstName} ${userData.peerUsers.lastName}`
        } \n <span>${messages.content.replaceAll('\n', ' ')}</span>`,
        image: userData.isGroupChat ? userData.logo : userData.peerUsers.logo,
        link,
        type: IN_APP_NOTIFICATION_TYPE.MESSAGE,
        isRead: false,
      };
      // Dispatch the notification action
      await dispatch(doAddNotification(notification));
      // Send the notification
      sendNotificationSticky(notification);
    } catch (error) {
      console.error('Failed to create and dispatch notification:', error);
      // Optional: Add fallback action here, e.g., log to a monitoring service
    }
  };

  useEffect(() => {
    if (notificationData && chatUser) {
      const userData = chatUser.find(
        (li) => li?.chatId === notificationData.chatId,
      );
      console.log('notificationData', notificationData);
      createAndDispatchNotification(
        notificationData.message,
        notificationData.chatId,
        userData,
      );
    }
  }, [notificationData, chatUser]);

  return (
    <AppShell>
      <BillingAttentionBanner />
      <PageContainer>
        <Modal />
        {isWide && <MainMenu />}
        <NotificationsListener />
        <Outlet />
        {appWidgetVisible ? <AppWidget /> : null}
      </PageContainer>
    </AppShell>
  );
};

const AppRouter = () => {
  const communityLink = commonGetStorageCommunity();

  return (
    <Routes>
      <Route
        path="/"
        element={
          communityLink ? (
            <Navigate to={communityLink} replace />
          ) : (
            <Navigate to="profile" replace />
          )
        }
      />
      <Route
        path={`${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.ENTERPRISE_CHECKOUT}`}
        element={<EnterpriseCheckoutPage />}
      />

      <Route
        path={`${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CANCEL_SUBSCRIPTION}`}
        element={<CancelSubscriptionPage />}
      />

      <Route
        path={`${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}/:subscriptionId`}
        element={<SubscriptionInner />}
      />

      <Route
        path={`${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`}
        element={<ChangePlanPage />}
      />

      <Route
        path={`${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.PLANS}/:subscriptionId`}
        element={<SubscriptionPlansToChangePlanCheckoutRedirect />}
      />

      <Route
        path={`${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.PLANS}`}
        element={<SubscriptionPlansListingRedirect />}
      />

      <Route path="*" element={<PageContainerWrapper />}>
        <Route path="profile/*" element={<ProfileRouter />} />
        <Route
          path="communities/:communityId/landing"
          element={<CommunityLandingWrapper />}
        />
        <Route
          path="community/:communityId/*"
          element={<CommunityRouterWrapper />}
        />
        <Route
          path="*"
          element={
            communityLink ? (
              <Navigate to={communityLink} replace />
            ) : (
              <Navigate to="profile" replace />
            )
          }
        />
      </Route>
    </Routes>
  );
};

export default AppRouter;
